import type {
  MarketplaceAdminEvents,
  MarketplaceEvents,
  MarketplaceProviderEvents,
} from 'usage-tracker-core/__generated__/usage-tracker-client-types/events';
import { MarketplaceEventProps, PartialPick } from './properties';

type TrackEventDefaultProps = keyof Pick<
  MarketplaceEventProps,
  'screen' | 'subscreen'
>;

type MarketplaceEventName =
  (typeof MarketplaceEvents)[keyof typeof MarketplaceEvents];

type MarketplaceProviderEventName =
  (typeof MarketplaceProviderEvents)[keyof typeof MarketplaceProviderEvents];

type MarketplaceAdminEventName =
  (typeof MarketplaceAdminEvents)[keyof typeof MarketplaceAdminEvents];

type TrackEventProps<TEvent extends Partial<MarketplaceEventProps>> = Omit<
  TEvent,
  TrackEventDefaultProps
> &
  PartialPick<TEvent, Extract<TrackEventDefaultProps, keyof TEvent>>;

export type MarketplaceEventConfigNamespace =
  | 'Marketplace'
  | 'Marketplace Provider'
  | 'Marketplace Admin';

export type MarketplaceEventNameFor<
  TNamespace extends MarketplaceEventConfigNamespace,
> = TNamespace extends 'Marketplace'
  ? MarketplaceEventName
  : TNamespace extends 'Marketplace Provider'
  ? MarketplaceProviderEventName
  : MarketplaceAdminEventName;

export interface MarketplaceEventConfig<
  TProps extends
    Partial<MarketplaceEventProps> = Partial<MarketplaceEventProps>,
  TNamespace extends
    MarketplaceEventConfigNamespace = MarketplaceEventConfigNamespace,
> {
  namespace: TNamespace;
  eventName: MarketplaceEventNameFor<TNamespace>;
  eventProps: TProps;
}

export type MarketplaceEventFactory<
  TProps extends Partial<MarketplaceEventProps>,
  TNamespace extends
    MarketplaceEventConfigNamespace = MarketplaceEventConfigNamespace,
> = (
  eventProps: TrackEventProps<TProps>
) => MarketplaceEventConfig<TrackEventProps<TProps>, TNamespace>;

export function createMarketplaceEventFactory<
  TProps extends Partial<MarketplaceEventProps>,
  TNamespace extends
    MarketplaceEventConfigNamespace = MarketplaceEventConfigNamespace,
>(
  namespace: TNamespace,
  eventName: MarketplaceEventNameFor<TNamespace>
): MarketplaceEventFactory<TProps, TNamespace> {
  return function (eventProps) {
    return {
      namespace,
      eventName,
      eventProps,
    };
  };
}
